/**
 * The alerts inbox (V056) on the client: types, the kinds' looks, and the
 * words for times. One inbox holds every alert: price, idea, strategy, from
 * the team, account, admin notices, Research Analysts' calls (V063) and
 * (friends only) AI paper trades.
 */
import {
  BellRing,
  CreditCard,
  Eye,
  Lightbulb,
  Megaphone,
  Newspaper,
  Radar,
  Bot,
  ShieldAlert,
  Target,
  TrendingDown,
  TrendingUp,
  UserCheck,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react";

import type { Schema } from "@/api/types";

export type AlertItem = Schema<"AlertItemOut">;
export type InboxSummary = Schema<"InboxSummaryOut">;
export type AlertSettings = Schema<"AlertSettingsOut">;
export type KindSetting = Schema<"AlertKindSettingOut">;
export type Group =
  | "price"
  | "ideas"
  | "strategies"
  | "radar"
  | "news"
  | "calls"
  | "clients"
  | "ai"
  | "team"
  | "account"
  | "system"
  | "watches";

export const SUMMARY_KEY = ["/me/inbox/summary"];
export const INBOX_KEY = ["/me/inbox"];
export const SETTINGS_KEY = ["/me/alert-settings"];

/** The groups in the order the popup and the history list them. */
export const GROUPS: { key: Group; label: string; short: string }[] = [
  { key: "price", label: "Price alerts", short: "Price" },
  { key: "ideas", label: "Idea alerts · Stock Strategy Lab", short: "Ideas" },
  {
    key: "strategies",
    label: "Strategy watches · Options Strategy Lab",
    short: "Strategies",
  },
  { key: "radar", label: "Market Radar", short: "Radar" },
  { key: "news", label: "Market news", short: "News" },
  // V063: calls from the member's Research Analysts, and their results
  { key: "calls", label: "Research Analyst calls", short: "Calls" },
  // V093: an RA's own updates (followers, clients, profile reviews)
  { key: "clients", label: "Followers and clients", short: "Clients" },
  { key: "ai", label: "AI · friends only", short: "AI" },
  // V105: the admin's own option watches (Admin -> Content -> Watch & alert)
  { key: "watches", label: "Watch and alert", short: "Watches" },
  { key: "team", label: "From the team", short: "Team" },
  { key: "account", label: "Account and payment", short: "Account" },
  { key: "system", label: "Admin notices", short: "Admin" },
];

export const GROUP_LABEL: Record<string, string> = Object.fromEntries(
  GROUPS.map((g) => [g.key, g.short]),
);

export type Tone = "brand" | "up" | "down" | "warn" | "neutral";

/** The tile behind a kind's icon, and the stripe on its card. */
export const TONE: Record<
  Tone,
  { tile: string; stripe: string; text: string }
> = {
  brand: {
    tile: "bg-brand-soft text-brand",
    stripe: "bg-brand",
    text: "text-brand",
  },
  up: { tile: "bg-up-soft text-up", stripe: "bg-up", text: "text-up" },
  down: {
    tile: "bg-down-soft text-down",
    stripe: "bg-down",
    text: "text-down",
  },
  warn: {
    tile: "bg-warn-soft text-warn",
    stripe: "bg-warn",
    text: "text-warn",
  },
  neutral: {
    tile: "bg-surface-2 text-muted",
    stripe: "bg-border-strong",
    text: "text-muted",
  },
};

export function toneOf(item: Pick<AlertItem, "tone">): Tone {
  return (["brand", "up", "down", "warn", "neutral"] as const).includes(
    item.tone as Tone,
  )
    ? (item.tone as Tone)
    : "brand";
}

/** The icon for an alert: by kind, and for AI trades by what happened. */
export function iconOf(
  item: Pick<AlertItem, "kind" | "tone" | "tag">,
): LucideIcon {
  switch (item.kind) {
    case "price":
      return BellRing;
    case "idea":
      return Lightbulb;
    case "strategy":
      return Target;
    case "ai_paper":
    case "paper_trade":
      if (item.tag.endsWith("Target hit")) return TrendingUp;
      if (item.tag.endsWith("Stop hit")) return TrendingDown;
      return Bot;
    case "market_radar":
      return Radar;
    case "news":
      return Newspaper;
    case "desk_view":
      return Bot;
    case "ra_call":
      if (item.tag.startsWith("Target hit")) return TrendingUp;
      if (item.tag.startsWith("Stop-loss hit")) return TrendingDown;
      return Megaphone;
    case "announcement":
      return Megaphone;
    case "service":
      return Wrench;
    case "account":
      return CreditCard;
    case "admin":
      return ShieldAlert;
    case "watch_alert":
      return Eye;
    default:
      if (item.kind.startsWith("ra_photo") || item.kind.startsWith("ra_verif"))
        return UserCheck;
      if (item.kind.startsWith("ra_")) return Users; // followers and clients (V093)
      return BellRing;
  }
}

/** The badge in the history's Type column. */
export const KIND_BADGE: Record<
  string,
  {
    label: string;
    tone: "brand" | "up" | "down" | "warn" | "neutral" | "violet" | "accent";
  }
> = {
  price: { label: "Price", tone: "down" },
  idea: { label: "Idea", tone: "brand" },
  strategy: { label: "Strategy", tone: "warn" },
  market_radar: { label: "Radar", tone: "accent" },
  news: { label: "News", tone: "brand" },
  desk_view: { label: "AI view", tone: "violet" },
  ra_call: { label: "RA call", tone: "up" },
  ai_paper: { label: "AI paper", tone: "violet" },
  paper_trade: { label: "Paper Trade", tone: "violet" },
  announcement: { label: "Team", tone: "warn" },
  service: { label: "Service", tone: "neutral" },
  account: { label: "Account", tone: "neutral" },
  admin: { label: "Admin", tone: "accent" },
  watch_alert: { label: "Watch", tone: "violet" },
  // V093: an RA's own updates
  ra_follow: { label: "Follower", tone: "up" },
  ra_unfollow: { label: "Follower", tone: "neutral" },
  ra_client_request: { label: "Client", tone: "brand" },
  ra_client_joined: { label: "Client", tone: "up" },
  ra_invite_declined: { label: "Client", tone: "neutral" },
  ra_client_left: { label: "Client", tone: "warn" },
  ra_client_limit: { label: "Client places", tone: "warn" },
  ra_photo_approved: { label: "Profile", tone: "up" },
  ra_photo_rejected: { label: "Profile", tone: "warn" },
  ra_verified: { label: "Profile", tone: "up" },
  ra_verify_rejected: { label: "Profile", tone: "warn" },
};

const IST = "Asia/Kolkata";
const dayFmt = new Intl.DateTimeFormat("en-IN", {
  timeZone: IST,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const timeFmt = new Intl.DateTimeFormat("en-IN", {
  timeZone: IST,
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});
const wdFmt = new Intl.DateTimeFormat("en-IN", {
  timeZone: IST,
  weekday: "short",
});
const dateFmt = new Intl.DateTimeFormat("en-IN", {
  timeZone: IST,
  weekday: "short",
  day: "numeric",
  month: "short",
});

function istDay(d: Date): string {
  return dayFmt.format(d);
}

/** "2:41 pm" (Indian time, lower case like the rest of the app). */
export function timeIST(value: string | Date): string {
  return timeFmt
    .format(new Date(value))
    .replace(/\s?(AM|PM)$/i, (m) => ` ${m.trim().toLowerCase()}`);
}

/** "Just now", "10 min ago", "Today 3:25 pm", "Fri 2:41 pm", "Thu 22 Oct". */
export function whenText(value: string, now: Date = new Date()): string {
  const d = new Date(value);
  const mins = Math.round((now.getTime() - d.getTime()) / 60_000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  if (istDay(d) === istDay(now)) return `Today ${timeIST(d)}`;
  if (now.getTime() - d.getTime() < 6 * 86_400_000)
    return `${wdFmt.format(d)} ${timeIST(d)}`;
  return dateFmt.format(d);
}

/** The history's day headings: "Today, Sun 25 Oct", "Fri 23 Oct". */
export function dayHeading(value: string, now: Date = new Date()): string {
  const d = new Date(value);
  const label = dateFmt.format(d).replace(",", "");
  return istDay(d) === istDay(now) ? `Today, ${label}` : label;
}

export function dayKey(value: string): string {
  return istDay(new Date(value));
}

/** The unread count as the bell shows it. */
export function countText(n: number): string {
  return n > 9 ? "9+" : String(n);
}

/** Inside the member's quiet hours now (no sound, no desktop notification)? */
export function inQuietHours(
  prefs: AlertSettings["prefs"] | undefined,
  now: Date = new Date(),
): boolean {
  if (!prefs?.quiet_enabled || prefs.quiet_from === prefs.quiet_to)
    return false;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(now);
  const toMin = (s: string) => {
    const [h, m] = s.split(":").map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
  };
  const t = toMin(parts);
  const a = toMin(prefs.quiet_from);
  const b = toMin(prefs.quiet_to);
  return a < b ? t >= a && t < b : t >= a || t < b;
}

/** The settings row that decides how an alert reaches the member. */
export function settingOf(kind: string): string {
  switch (kind) {
    case "ai_paper":
    case "paper_trade":
      return "ai";
    case "announcement":
    case "service":
      return "team";
    case "market_radar":
      return "radar";
    case "ra_call":
      return "calls";
    default:
      return kind;
  }
}

export function channelOn(
  settings: AlertSettings | undefined,
  kind: string,
  channel: "card" | "popup" | "email" | "desktop",
): boolean {
  const row = settings?.kinds.find((k) => k.kind === settingOf(kind));
  return row ? Boolean(row[channel]) : channel !== "desktop";
}

/** An alert with every field filled in (previews in the admin composer, tests). */
export function blankItem(
  over: Partial<AlertItem> & Pick<AlertItem, "id" | "kind" | "title">,
): AlertItem {
  return {
    event_id: over.id,
    group: "price",
    tag: "Price alert",
    created_at: "2026-10-23T09:11:00Z",
    body: null,
    figure: null,
    chips: [],
    more: 0,
    link: "/app/alerts",
    action: "Open",
    tone: "brand",
    note: null,
    paper: false,
    friends_note: null,
    snooze: false,
    sound: false,
    card: true,
    sticky: true,
    show_until: null,
    seen: false,
    read: false,
    dismissed: false,
    snoozed_until: null,
    email_status: null,
    desktop: false,
    clicked: false,
    ...over,
  };
}
